import React, { useEffect, useRef } from 'react';
import { Target, Copy, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MENU_WIDTH = 220;

const MapContextMenu = ({ position, coords, onClose }) => {
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [onClose]);

  if (!position || !coords) return null;

  // Keep menu inside viewport
  const left = Math.min(position.x, window.innerWidth - MENU_WIDTH - 12);
  const top = position.y + 10;

  const handleCreateOpportunity = () => {
    onClose();
    navigate(`/opportunities/new?lat=${coords.lat}&lng=${coords.lng}`);
  };

  const handleCopyCoords = () => {
    const text = `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
    navigator.clipboard.writeText(text).catch(() => {});
    onClose();
  };

  const handleOpenGoogleMaps = () => {
    window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const menuItems = [
    {
      icon: Target,
      label: 'Crear Oportunidad aquí',
      color: '#E27B58',
      action: handleCreateOpportunity,
      primary: true,
    },
    {
      icon: Copy,
      label: 'Copiar coordenadas',
      color: '#00CEC9',
      action: handleCopyCoords,
    },
    {
      icon: ExternalLink,
      label: 'Ver en Google Maps',
      color: '#9B59B6',
      action: handleOpenGoogleMaps,
    },
  ];

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left,
        top,
        width: MENU_WIDTH,
        background: 'rgba(7, 10, 18, 0.97)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(226, 123, 88, 0.4)',
        borderRadius: '10px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.8), 0 0 20px rgba(226,123,88,0.1)',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '10px 14px 8px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        fontSize: '0.65rem',
        color: '#555',
        fontFamily: 'monospace',
        letterSpacing: '1px',
      }}>
        {coords.lat.toFixed(5)}°N · {coords.lng.toFixed(5)}°E
      </div>

      {/* Items */}
      <div style={{ padding: '6px 0' }}>
        {menuItems.map(({ icon: Icon, label, color, action, primary }) => (
          <button
            key={label}
            onClick={action}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '10px 14px',
              background: primary ? 'rgba(226,123,88,0.08)' : 'transparent',
              border: 'none',
              borderLeft: primary ? `3px solid ${color}` : '3px solid transparent',
              color: primary ? color : '#CCC',
              cursor: 'pointer',
              fontSize: '0.85rem',
              textAlign: 'left',
              transition: 'background 0.15s, color 0.15s',
              fontWeight: primary ? '700' : '400',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = `${color}18`;
              e.currentTarget.style.color = color;
              e.currentTarget.style.borderLeftColor = color;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = primary ? 'rgba(226,123,88,0.08)' : 'transparent';
              e.currentTarget.style.color = primary ? color : '#CCC';
              e.currentTarget.style.borderLeftColor = primary ? color : 'transparent';
            }}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default MapContextMenu;
